/**
 * Onboarding/dashboard home. Each tile is a clear "do this next" call to
 * action with a step number that maps to the recommended order:
 *
 *   1. Build hierarchy (constituencies → wards → branches)
 *   2. Define roles (or use the system roles)
 *   3. Invite users
 *   4. Assign roles + scope
 *   5. Then operational work begins (cases, intel, tasks, etc.)
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { NothingToSeeHere } from "./NothingToSeeHere";
import type { MenuNode } from "./Sidebar";
import {
  BadgeIcon,
  BarChartIcon,
  BuildingIcon,
  CheckSquareIcon,
  ClipboardIcon,
  EyeIcon,
  FileIcon,
  RadioIcon,
  ShieldIcon,
  StarIcon,
  TrendingIcon,
  UsersIcon,
} from "./icons";

interface Tile {
  step?: string;
  title: string;
  desc: string;
  to: string;
  icon: React.ReactNode;
}

const SETUP_TILES: Tile[] = [
  {
    step: "Step 1",
    title: "Build the hierarchy",
    desc: "Create constituencies, then wards, then branches. Everything else attaches to this.",
    to: "/app/admin/hierarchy",
    icon: <BuildingIcon />,
  },
  {
    step: "Step 2",
    title: "Review roles",
    desc: "Four system roles are pre-loaded. Customise their permissions or create new ones.",
    to: "/app/admin/roles",
    icon: <ShieldIcon />,
  },
  {
    step: "Step 3",
    title: "Create people + assign access",
    desc: "Each user is created with their first role + scope in the same flow. Edit access later from the same row.",
    to: "/app/admin/users",
    icon: <UsersIcon />,
  },
];

const OPS_TILES: Tile[] = [
  { title: "Tasks", desc: "Assign work to officers and track progress.", to: "/app/tasks", icon: <CheckSquareIcon /> },
  { title: "Intel feed", desc: "Live stream of observations, alerts, and notes.", to: "/app/intel", icon: <RadioIcon /> },
  { title: "Cases", desc: "Operational case records with classification.", to: "/app/cases", icon: <FileIcon /> },
  { title: "People of Interest", desc: "Watchlist of subjects across your scope.", to: "/app/poi", icon: <EyeIcon /> },
  { title: "Performance", desc: "Reports on member performance, with reasons.", to: "/app/performance", icon: <TrendingIcon /> },
  { title: "Popularity", desc: "Sentiment readings on subjects in your area.", to: "/app/popularity", icon: <StarIcon /> },
];

const INSIGHTS_TILES: Tile[] = [
  { title: "Reports", desc: "Summary dashboards + raw data export.", to: "/app/reports", icon: <BarChartIcon /> },
  { title: "Audit log", desc: "Every privileged action, append-only.", to: "/app/audit", icon: <ClipboardIcon /> },
];

export function Home({
  superadmin,
  email,
  menu,
}: {
  superadmin: boolean;
  email?: string | null;
  menu?: MenuNode[];
}) {
  // For non-superadmin users with no permitted screens, the dashboard tiles
  // would all 403. Show the friendly empty state instead — they take it up
  // with their senior.
  const hasAccess = superadmin || (menu && menu.length > 0);
  if (!hasAccess) {
    return (
      <NothingToSeeHere subtitle="Your account has no access yet. Ask your senior to give you a role and scope." />
    );
  }
  // Filter operations + insights tiles to only those whose route matches a
  // screen the user can access. Setup tiles stay visible only for superadmins.
  const allowed = new Set<string>();
  function walk(n: MenuNode) {
    if (n.screenKey) allowed.add(`/app/${n.screenKey}`);
    n.children.forEach(walk);
  }
  (menu ?? []).forEach(walk);
  const showTile = (t: Tile) => superadmin || allowed.has(t.to);
  const visibleSetup = superadmin ? SETUP_TILES : [];
  const visibleOps = OPS_TILES.filter(showTile);
  const visibleInsights = INSIGHTS_TILES.filter(showTile);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 600 }}>
            Welcome{email ? `, ${email.split("@")[0]}` : ""}
          </h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            {superadmin
              ? "You're a superadmin. Start by building the hierarchy below — everything else flows from it."
              : "Pick a section below to get to work."}
          </p>
        </div>
        <Link to="/app/new" style={{ textDecoration: "none" }}>
          <button style={{ padding: "10px 18px", fontSize: 14 }}>+ New</button>
        </Link>
      </div>

      <div style={{ marginBottom: 28 }} />

      {visibleSetup.length > 0 && (
        <>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 12px" }}>
            Get started
          </h2>
          <div className="tiles">
            {visibleSetup.map((t) => <TileCard key={t.to} tile={t} />)}
          </div>
        </>
      )}

      {visibleOps.length > 0 && (
        <>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "32px 0 12px" }}>
            Operations
          </h2>
          <div className="tiles">
            {visibleOps.map((t) => <TileCard key={t.to} tile={t} />)}
          </div>
        </>
      )}

      {visibleInsights.length > 0 && (
        <>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "32px 0 12px" }}>
            Insights
          </h2>
          <div className="tiles">
            {visibleInsights.map((t) => <TileCard key={t.to} tile={t} />)}
          </div>
        </>
      )}

      <div className="card" style={{ marginTop: 32 }}>
        <h3 className="card-title">Need a hand?</h3>
        <p className="card-sub" style={{ margin: 0 }}>
          The audit log records every action. Reports give you aggregates. Both are read-only and safe to explore.
        </p>
      </div>

      {superadmin && <SeedDummyDataCard />}
      {superadmin && <EncryptionProofCard />}
    </div>
  );
}

function EncryptionProofCard() {
  const COLLECTIONS = [
    "cases", "intelItems", "peopleOfInterest", "tasks",
    "performanceReports", "popularityReports", "users", "people",
  ];
  const [coll, setColl] = useState("cases");
  const [limit, setLimit] = useState(3);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDump = useMutation({
    mutationFn: () =>
      api<{ collection: string; count: number; items: unknown[]; explanation: string }>(
        `/admin/raw-dump?collection=${encodeURIComponent(coll)}&limit=${limit}`,
      ),
    onSuccess: (r) => { setResult(JSON.stringify(r, null, 2)); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  function downloadAsJson() {
    if (!result) return;
    const blob = new Blob([result], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `encrypted-${coll}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 className="card-title">Verify encryption at rest</h3>
      <p className="card-sub" style={{ margin: "0 0 12px" }}>
        Pull a few documents straight from Firestore <strong>without decryption</strong>. Encrypted
        fields appear inside an <code>_enc</code> envelope (<code>iv</code> / <code>ct</code> / <code>dek</code>) — you'll see ciphertext,
        not plaintext, which is exactly what an attacker with read access to the database would see.
        The KMS key needed to unwrap is held in Google Cloud KMS and is access-gated separately.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={coll} onChange={(e) => setColl(e.target.value)}>
          {COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="number"
          min={1}
          max={20}
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(20, Number(e.target.value) || 3)))}
          style={{ width: 80 }}
        />
        <button onClick={() => fetchDump.mutate()} disabled={fetchDump.isPending}>
          {fetchDump.isPending ? "Fetching…" : "Show raw documents"}
        </button>
        {result && <button className="ghost" onClick={downloadAsJson}>Download JSON</button>}
      </div>
      {error && <p className="error" style={{ marginTop: 8 }}>{error}</p>}
      {result && (
        <pre style={{
          marginTop: 12,
          maxHeight: 360,
          overflow: "auto",
          background: "#0b1220",
          color: "#e2e8f0",
          padding: 12,
          borderRadius: 6,
          fontSize: 11,
          lineHeight: 1.4,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}>{result}</pre>
      )}
    </div>
  );
}

function SeedDummyDataCard() {
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  interface SeedResponse {
    created: Record<string, number>;
    skipped: number;
    wiped?: Record<string, number>;
    capturer?: string;
    seeded_user_password?: string;
  }
  const seed = useMutation({
    mutationFn: (reset: boolean) =>
      // Seed can take a while (Firebase Auth user creation + dozens of writes
      // through the encryption layer). Let it run for up to 4 minutes before
      // we declare it timed out — the previous 30s default was too short.
      api<SeedResponse>(`/admin/seed-dummy-data${reset ? "?reset=1" : ""}`, { method: "POST", timeoutMs: 240_000 }),
    onSuccess: (r) => {
      const total = Object.values(r.created).reduce((a, b) => a + b, 0);
      const wipedTotal = Object.values(r.wiped ?? {}).reduce((a, b) => a + b, 0);
      const cap = r.capturer ? ` Captured as ${r.capturer}.` : "";
      const pw = r.seeded_user_password ? ` Seeded users sign in with: ${r.seeded_user_password}.` : "";
      setResult(`${wipedTotal ? `Wiped ${wipedTotal} previous seed records. ` : ""}Created ${total} records (${r.skipped} already existed).${cap}${pw}`);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div className="card" style={{ marginTop: 16, borderColor: "var(--accent-soft)" }}>
      <h3 className="card-title">Seed dummy data</h3>
      <p className="card-sub" style={{ margin: "0 0 12px" }}>
        Populate the system with an intelligence-flavoured starter dataset (hierarchy, sample cases, intel,
        POI, performance + popularity reports, tasks) and seed three test user accounts. Records are captured
        as <code>sp@gmail.com</code> if that account exists; otherwise the seeder. PII is encrypted at rest.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={() => {
            if (window.confirm("Seed dummy data? This writes sample records into the live database. Existing seed records are kept. Audit-logged."))
              seed.mutate(false);
          }}
          disabled={seed.isPending}
        >
          {seed.isPending ? "Seeding…" : "Seed (keep existing)"}
        </button>
        <button
          className="danger"
          onClick={() => {
            if (window.confirm("Reset and re-seed? This DELETES every existing seed record (only seed-* IDs) and rebuilds from scratch. Real user records are preserved. Audit-logged."))
              seed.mutate(true);
          }}
          disabled={seed.isPending}
        >
          Reset and re-seed
        </button>
      </div>
      {result && <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>{result}</p>}
      {error && <p className="error" style={{ marginTop: 8 }}>{error}</p>}
    </div>
  );
}

function TileCard({ tile }: { tile: Tile }) {
  return (
    <Link to={tile.to} className="tile">
      <div className="tile-icon">{tile.icon}</div>
      {tile.step && <div className="tile-step">{tile.step}</div>}
      <div className="tile-title">{tile.title}</div>
      <div className="tile-desc">{tile.desc}</div>
    </Link>
  );
}
